<template>
  <v-dialog
    v-model="dialog"
    max-width="500px"
  >
    <v-card>
      <v-form
        v-model="valid"
        @submit.prevent="save"
      >
        <v-card-title>
          <span class="headline">Edit rate</span>
          <v-chip
            v-if="copyOfRate.segment"
            :color="copyOfRate.segment === 'FIRST_TRANSACTION' ? 'amber lighten-2' : 'blue lighten-4'"
            small
            class="ml-3"
          >
            {{ copyOfRate.segment === 'FIRST_TRANSACTION' ? 'First Transaction' : 'Standard' }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="copyOfRate.marginDesired"
                  label="Desired mark-up from provider rate"
                  suffix="%"
                  :rules="marginDesiredRules"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  ref="trRateGivenToCustomer"
                  v-model="copyOfRate.manualRate"
                  label="Customer Rate"
                  :rules="ruleCustomRate"
                  :disabled="copyOfRate.automaticallyRate"
                />
              </v-col>
              <v-col
                v-if="copyOfRate.segment !== 'FIRST_TRANSACTION'"
                cols="12"
              >
                <v-switch
                  v-model="marginRangesEnabled"
                  inset
                  dense
                  label="Enable tiered mark-up"
                  @change="onToggleMarginRanges"
                />
              </v-col>
              <v-col
                v-if="marginRangesEnabled"
                cols="12"
              >
                <div
                  v-for="(tier, index) in copyOfRate.marginDesiredRanges"
                  :key="`margin-tier-edit-${index}`"
                  class="margin-tier-card"
                >
                  <v-btn
                    v-if="index > 0"
                    class="margin-tier-card__remove"
                    icon
                    plain
                    x-small
                    @click="onRemoveMarginRange(index)"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-row>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model.number="tier.minAmount"
                        type="number"
                        min="0.01"
                        label="Min amount"
                        :rules="tierMinAmountRules(tier, index)"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      sm="6"
                    >
                      <v-text-field
                        v-model.number="tier.marginPercent"
                        type="number"
                        min="0"
                        max="100"
                        label="Desired mark-up"
                        suffix="%"
                        :rules="tierMarginRules(tier)"
                      />
                    </v-col>
                  </v-row>
                </div>
                <v-btn
                  v-if="copyOfRate.marginDesiredRanges.length < marginRangesLimit"
                  class="mt-2"
                  color="primary"
                  outlined
                  block
                  @click="onAddMarginRange"
                >
                  <v-icon left>
                    mdi-plus
                  </v-icon>
                  Add mark-up tier
                </v-btn>
              </v-col>
              <template v-if="copyOfRate.segment !== 'FIRST_TRANSACTION' && legacyFirstTransactionEnabled">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    v-model="copyOfRate.firstTransactionRateDiscount"
                    label="1st Transaction rate"
                    suffix="%"
                    :rules="ruleFirstTransactionRateDiscount"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-text-field
                    ref="trRateGivenToCustomer"
                    v-model="copyOfRate.manualDiscountedUserRate"
                    label="Custom 1st transaction rate"
                    :rules="ruleCustomFirstRate"
                    :disabled="copyOfRate.automaticallyRate"
                  />
                </v-col>
              </template>
              <v-col
                cols="12"
                sm="6"
              >
                <v-checkbox
                  v-model="copyOfRate.automaticallyRate"
                  label="Rate calculated automatically"
                />
              </v-col>
              <v-col
                v-if="copyOfRate.segment !== 'FIRST_TRANSACTION' && legacyFirstTransactionEnabled"
                cols="12"
                sm="6"
              >
                <v-checkbox
                  v-model="copyOfRate.firstTransactionRateDiscountEnabled"
                  label="Enable 1st Transaction Rate"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn @click="close">
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            color="primary"
            :loading="loading"
            :disabled="loading || !valid"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { toFloat } from '@/utils/helpers';

export default {
  name: "EditRateModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    rate: {
      type: Object,
      default: () => ({})
    }
  },
  data: () => ({
    valid: false,
    copyOfRate: {},
    loading: false,
    marginRangesEnabled: false,
    marginRangesLimit: 50,
    ruleFirstTransactionRateDiscount: [],
    ruleCustomRate: [],
    ruleCustomFirstRate: [],
    legacyFirstTransactionEnabled: process.env.VUE_APP_LEGACY_FIRST_TRANSACTION_ENABLED !== 'false'
  }),
  computed: {
    marginDesiredRules() {
      return this.marginRangesEnabled && this.copyOfRate.segment !== 'FIRST_TRANSACTION'
        ? [value => this.hasValue(value) || 'Base desired mark-up is required']
        : [];
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    }
  },
  watch: {
    rate: {
      handler: function() {
        this.updateCopyOfRate();
      },
      deep: true
    },
    "copyOfRate.automaticallyRate"() {
      if (this.copyOfRate.automaticallyRate) {
        this.copyOfRate.calcEndUserRate = this.copyOfRate.marginDesired;
        this.ruleCustomRate = [];
        this.ruleCustomFirstRate = [];
      } else {
        this.ruleCustomRate = [
          v => !!v || 'Custom rate is required',
        ];

        if (this.copyOfRate.firstTransactionRateDiscountEnabled) {
          this.ruleCustomFirstRate = [
            v => !!v || 'This field is required',
          ];
        }
      }
    },
    "copyOfRate.calcEndUserRate"() {
      if (this.$refs.trRateGivenToCustomer?.isFocused) {
        this.copyOfRate.automaticallyRate = false;
      }
    },
    "copyOfRate.firstTransactionRateDiscountEnabled"() {
      if (this.copyOfRate.firstTransactionRateDiscountEnabled) {
        this.ruleFirstTransactionRateDiscount = [
          v => !!v || '1st Transaction rate is required',
        ];

        if (!this.copyOfRate.automaticallyRate) {
          this.ruleCustomFirstRate = [
            v => !!v || 'This field is required',
          ];
        }
      } else {
        this.ruleFirstTransactionRateDiscount = [];
        this.ruleCustomFirstRate = [];
      }
    }
  },
  methods: {
    save() {
      if (!this.tierConfigurationValid()) {
        return;
      }
      this.loading = true;
      const isFt = this.copyOfRate.segment === 'FIRST_TRANSACTION';
      const data = {
        sendingCountryIso3: this.copyOfRate.sendingCountryIso3,
        sendingCurrency: this.copyOfRate.sendingCurrency,
        receiveCountryIso3: this.copyOfRate.receiveCountryIso3,
        receiveCurrency: this.copyOfRate.receiveCurrency,
        sendingCurrency: this.copyOfRate.sendingCurrency,
        serviceCode: this.copyOfRate.serviceCode,
        segment: this.copyOfRate.segment,
        manualRate: this.copyOfRate.automaticallyRate
          ? null
          : toFloat(this.copyOfRate.manualRate),
        marginDesired: toFloat(this.copyOfRate.marginDesired),
        marginDesiredRanges: this.buildMarginRanges(),
        firstTransactionRateDiscountEnabled: isFt ? false : this.copyOfRate.firstTransactionRateDiscountEnabled,
        manualDiscountedUserRate: isFt || this.copyOfRate.automaticallyRate
          ? null
          : toFloat(this.copyOfRate.manualDiscountedUserRate),
        firstTransactionRateDiscount: isFt ? null : this.copyOfRate.firstTransactionRateDiscount
      }
      this.$store
        .dispatch("RecordStore/update", data)
        .then(() => {
          this.$emit("save", this.copyOfRate);
          this.close();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updateCopyOfRate() {
      this.$nextTick().then(() => {
        const clone = JSON.parse(JSON.stringify(this.rate));
        if (!Array.isArray(clone.marginDesiredRanges)) {
          clone.marginDesiredRanges = [];
        }
        this.$set(this, "copyOfRate", clone);
        this.marginRangesEnabled = clone.segment !== 'FIRST_TRANSACTION' && clone.marginDesiredRanges.length > 0;
      });
    },
    onToggleMarginRanges(value) {
      if (value && this.copyOfRate.marginDesiredRanges.length === 0) {
        this.onAddMarginRange();
      }
    },
    onAddMarginRange() {
      if (!Array.isArray(this.copyOfRate.marginDesiredRanges)) {
        this.$set(this.copyOfRate, "marginDesiredRanges", []);
      }
      if (this.copyOfRate.marginDesiredRanges.length >= this.marginRangesLimit) {
        return;
      }
      this.copyOfRate.marginDesiredRanges.push({ minAmount: null, marginPercent: null });
    },
    onRemoveMarginRange(index) {
      this.copyOfRate.marginDesiredRanges.splice(index, 1);
    },
    buildMarginRanges() {
      if (!this.marginRangesEnabled || this.copyOfRate.segment === 'FIRST_TRANSACTION') {
        return [];
      }
      return (this.copyOfRate.marginDesiredRanges || [])
        .map(tier => ({
          minAmount: toFloat(tier.minAmount),
          marginPercent: toFloat(tier.marginPercent)
        }))
        .sort((a, b) => a.minAmount - b.minAmount);
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    tierConfigurationValid() {
      if (!this.marginRangesEnabled || this.copyOfRate.segment === 'FIRST_TRANSACTION') {
        return true;
      }
      if (!this.hasValue(this.copyOfRate.marginDesired)) {
        return false;
      }
      const ranges = this.copyOfRate.marginDesiredRanges || [];
      if (ranges.length === 0) {
        return false;
      }
      const amounts = ranges.map(tier => Number(tier?.minAmount));
      return ranges.every((tier, index) => {
        const minAmount = Number(tier?.minAmount);
        const marginPercent = Number(tier?.marginPercent);
        return this.hasValue(tier?.minAmount) && minAmount > 0 &&
          amounts.indexOf(minAmount) === index &&
          this.hasValue(tier?.marginPercent) && marginPercent >= 0 && marginPercent <= 100;
      });
    },
    tierMinAmountRules(tier, index) {
      return [
        value => this.hasValue(value) || 'Min amount is required',
        value => Number(value) > 0 || 'Min amount must be greater than 0',
        value => (this.copyOfRate.marginDesiredRanges || []).findIndex(
          range => this.hasValue(range?.minAmount) && Number(range.minAmount) === Number(value)
        ) === index || 'Min amount must be unique'
      ];
    },
    tierMarginRules(tier) {
      return [
        () => this.hasValue(tier?.marginPercent) || 'Desired mark-up is required',
        () => {
          const value = Number(tier?.marginPercent);
          return value >= 0 && value <= 100 || 'Desired mark-up must be between 0 and 100';
        }
      ];
    },
    close() {
      this.dialog = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.margin-tier-card {
  position: relative;
  margin-bottom: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 12px;

  &__remove {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
  }
}
</style>
